/**
 * Analytics values shared by the show carousel and its media modal.
 *
 * WAT-12478 — clicks are reported by GTM auto-capture from the clicked element's
 * `aria-label` / `data-linkposition` / `data-linkdestination` (src/utils/link-click.ts):
 * the card carries them (getAnalyticsAttrs in show-carousal.tsx / show-carousal-parallax.tsx),
 * and so do the modal's CTAs and close icon (dtv-media-modal.tsx). Site code pushes a
 * `link_click` only for activations with no DOM click (AC10): a card activated from the
 * keyboard (reportShowCardKeyboardClick below) and a modal closed with Escape or the
 * backdrop (dtv-media-modal.tsx). Both paths have to agree on the same position /
 * destination strings, so they are built here rather than hand-rolled in each file.
 *
 * Text: modal positions go through analyticsText (lower case, whitespace collapsed). The
 * card's name does not — its attribute is the title lower-cased only, while the keyboard
 * push is normalised by fireLinkClick (trimmed, whitespace collapsed) — so the two differ
 * only for a title with surrounding or repeated whitespace.
 *
 * Destinations: SHOW_CAROUSEL_CARD_DESTINATION is a fixed relative path; currentPageUrl
 * (src/utils/link-click.ts) and getShowModalPageUrl build origin + pathname against the
 * current page and keep its case. Every `link_click` value — attribute or push — is reported lower case
 * (AC9); the AC2 page_view below is not a link click and keeps the path's case (AC12).
 *
 * The page_view goes out through the service's fireDataLayerEvent rather than a raw
 * `dataLayer.push`: a raw push would skip the parent-window handling the Storyblok visual
 * editor needs (the editor renders the page in an iframe, so an un-forwarded push lands on a
 * dataLayer no GTM container is listening to), and it would skip the app's post-paint
 * analytics queue, which a bare import of the service resolves to (the exact-match webpack
 * alias in next.config.js, mirrored by jest's moduleNameMapper).
 */
import { fireDataLayerEvent } from "@directv-digital/dds.services.analytics-service";
import { fireLinkClick } from "src/utils/link-click";

export const SHOW_CAROUSEL_CARD_POSITION = "panel_show carousel";

/**
 * Destination reported for a card click. Governance describes modal destinations as
 * `/modal/[identifier]`; this component reports the form the analytics team keyed
 * GA reporting to for the show carousel.
 */
export const SHOW_CAROUSEL_CARD_DESTINATION = "/modal-show_carousel";

const SHOW_MODAL_POSITION = "modal_show carousel";

const MODAL_PAGE_URL_SUFFIX = "/show_carousel";

// Reported names and positions are lower case, with interior whitespace collapsed: they are
// CMS/OV strings, and a stray newline or double space splits what should be one dimension
// value into several rows in the report. A multi-line show name is not hypothetical — the
// modal renders the heading with whiteSpace: "pre-wrap" precisely so it can carry one.
// A non-string must not throw: the heading reaches us from an untyped payload, and this runs
// during render, ahead of the modal's hooks. URLs deliberately do NOT go through this: their
// case must survive.
const analyticsText = (value?: string): string => (
  typeof value === "string" ? value.replace(/\s+/g, " ").trim().toLowerCase() : ""
);

/**
 * `modal_show carousel_yellowstone` — the position reported for every interaction
 * inside the show modal. Falls back to the bare modal identifier when the show name
 * is not available, so the value is never blank.
 */
export const getShowModalPosition = (showName?: string): string => {
  const name = analyticsText(showName);
  return name ? `${SHOW_MODAL_POSITION}_${name}` : SHOW_MODAL_POSITION;
};

/**
 * Current page URL with the modal suffix appended, used as the modal's page view URL.
 * Returns an empty string on the server so nothing is reported before hydration.
 *
 * The path is passed through with its case intact: this is the AC2 page_view url, which
 * AC12 leaves unchanged.
 */
export const getShowModalPageUrl = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const pageUrl = `${window.location.origin}${window.location.pathname}`.replace(
    /\/+$/,
    ""
  );
  return `${pageUrl}${MODAL_PAGE_URL_SUFFIX}`;
};

/**
 * Reports a card click the GTM click capture cannot see. Activating a role="button" card
 * from the keyboard fires no DOM click, so the card's data attributes are never read —
 * call this from keydown handlers only, or a mouse click is counted twice.
 *
 * WAT-12478 — an AC10 exception, so it goes out as `link_click` through fireLinkClick, not the
 * retired `custom_link_click`. Position and destination are the card's own attribute values;
 * the name is the same title, but trimmed and whitespace-collapsed by fireLinkClick, where the
 * card's aria-label is only lower-cased (see the header).
 */
export const reportShowCardKeyboardClick = (showName?: string): void => {
  const linkName =
    typeof showName === "string" ? showName.trim().toLowerCase() : "";
  if (!linkName) {
    return;
  }
  fireLinkClick({
    linkName,
    linkPosition: SHOW_CAROUSEL_CARD_POSITION,
    linkDestination: SHOW_CAROUSEL_CARD_DESTINATION
  });
};

// The emitter is a no-op on the server. fireDataLayerEvent dereferences `window`
// unguarded, and the webpack alias that routes this import through the app's post-paint
// queue is client-bundles-only — so on the server this import IS the raw DDS function
// and an unguarded call would throw during SSR.
const push = (payload: Record<string, unknown>): void => {
  if (typeof window === "undefined") {
    return;
  }
  fireDataLayerEvent(payload);
};

/**
 * AC2 — the modal's own page view. No linkName: the AC defines none. `event` is
 * `page_view` rather than the `trackEvent` the rest of this component uses, which is the
 * AC's own wording.
 */
export const fireShowModalPageView = (showName?: string): void => push({
  event: "page_view",
  event_name: "page_view",
  linkPosition: getShowModalPosition(showName),
  linkDestination: getShowModalPageUrl()
});

